/**
 * Date utility functions for RSTLNE
 * Handles date operations for daily challenges and streaks
 * 
 * CRITICAL: These functions replicate the original DateUtil behavior exactly
 * to maintain functional parity with timezone-aware date handling
 */

/**
 * Get local date as ISO string with timezone offset correction
 * This replicates the original DateUtil.getLocalDateISOString() behavior
 */
export const getLocalDateISOString = (dayOffset: number = 0): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000; // Convert minutes to milliseconds
  const localDate = new Date(now.getTime() - offset);
  localDate.setDate(localDate.getDate() + dayOffset);
  return localDate.toISOString().slice(0, -1); // Remove the 'Z' at the end
};

/**
 * Get today's date as a string (YYYY-MM-DD format) using local timezone
 */
export const getTodayString = (): string => {
  return getLocalDateISOString().split('T')[0];
};

/**
 * Get yesterday's date as a string (YYYY-MM-DD format) using local timezone
 */
export const getYesterdayString = (): string => {
  return getLocalDateISOString(-1).split('T')[0];
};

/**
 * Check if two date strings represent the same day
 */
export const isSameDay = (date1: string, date2: string): boolean => {
  return date1 === date2;
};

/**
 * Check if a date string is today
 */
export const isToday = (dateString: string): boolean => {
  return isSameDay(dateString, getTodayString());
};

/**
 * Check if a date string is yesterday
 */
export const isYesterday = (dateString: string): boolean => {
  return isSameDay(dateString, getYesterdayString());
};

/**
 * Calculate the number of days between two dates
 * Returns positive number if date2 is after date1
 */
export const daysBetween = (date1: string, date2: string): number => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const timeDiff = d2.getTime() - d1.getTime();
  return Math.floor(timeDiff / (1000 * 60 * 60 * 24));
};

/**
 * Check if dates are consecutive (date2 is the day after date1)
 */
export const areConsecutiveDays = (date1: string, date2: string): boolean => {
  return daysBetween(date1, date2) === 1;
};

/**
 * Format a date string for display
 */
export const formatDateForDisplay = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format a date string for results display and sharing (includes weekday)
 * Used in Results component and sharing functionality across platforms
 */
export const formatDateForResults = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC'
  });
};

/**
 * @deprecated Use formatDateForResults instead
 * Keeping for backward compatibility during transition
 */
export const formatDateForSharing = formatDateForResults;

/**
 * Get a consistent seed for a given date (for deterministic word selection)
 * This ensures all players get the same word on the same day
 */
export const getDateSeed = (dateString: string): number => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 0-indexed, so add 1
  const day = date.getDate();
  
  // Create a simple hash from date components
  return year * 10000 + month * 100 + day;
};

/**
 * Get the current date in ISO format (for storage)
 */
export const getCurrentISODate = (): string => {
  return new Date().toISOString();
};

/**
 * Parse a date string and return a Date object
 */
export const parseDate = (dateString: string): Date => {
  return new Date(dateString);
};

/**
 * Check if a date string is valid
 */
export const isValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};
